'use client';

import { useEffect } from 'react';

import { Typography } from '@mui/material';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import styled from 'styled-components';

import { useShowOlderVersions } from './ShowOlderVersionsProvider';
import Button from '@/ui/components/Button';
import { slugify } from '@/utils/slugify';

const DownloadWrap = styled.div<{ $handbook?: boolean }>`
  grid-column: ${({ $handbook }) => ($handbook ? `2 / 3` : `unset`)};

  ${({ theme }) => theme.breakpoints.down('md')} {
    margin-top: 40px;
    width: auto;
  }
`;

const DownloadSeparator = styled.div`
  &:not(:first-child) {
    border-top: 1px #e0e0e0 solid;
    margin-top: 30px;
    padding-top: 30px;
  }
`;

const DownloadDescription = styled.div`
  p,
  ul {
    color: #6f6f6f;
    margin-bottom: 0;
    margin-top: 10px;
  }
`;

const DownloadDate = styled.div`
  color: #6f6f6f;
  margin-bottom: 20px;
  margin-top: 10px;
`;

const DownloadLinks = styled.div`
  display: flex;
  gap: 30px;
  margin-top: 20px;

  a {
    cursor: pointer;
    text-decoration: underline;
  }
`;

const DownloadButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 15px; // 30px;
  margin-bottom: 20px;

  ${({ theme }) => theme.breakpoints.down('md')} {
    display: flex;
    gap: 20px;

    div + div {
      margin-top: 0;
    }
  }
`;

interface DownloadVersionProps {
  items: DownloadItem[];
  type: string;
  productSlug: string;
}

export default function DownloadVersion({ items, type, productSlug }: DownloadVersionProps) {
  const t = useTranslations();
  const { showOlderVersions, setShowOlderVersions } = useShowOlderVersions();

  // On mount, check if URL contains the query parameter "old" and set default state.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('versions')) {
      setShowOlderVersions(true);
    }
  }, [setShowOlderVersions]);

  function updateOldQuery(newShow: boolean) {
    setShowOlderVersions(newShow);

    // Split the pathname and remove empty parts.
    const pathParts = typeof window !== 'undefined' && window.location.pathname.split('/').filter(Boolean);
    // Find the index of "downloads" in the URL parts.
    if (!pathParts) return;

    const downloadsIndex = pathParts.findIndex(part => part === 'downloads');
    // Check if there is a slug after "downloads"
    if (downloadsIndex >= 0 && pathParts.length > downloadsIndex + 1) {
      const url = new URL(window.location.href);
      if (newShow) {
        url.searchParams.set('versions', 'all');
      } else {
        url.searchParams.delete('versions');
      }
      window.history.pushState({}, '', url.toString());
    }
  }

  return (
    <DownloadWrap $handbook={type === 'handbook'}>
      {items
        .filter((item, index) => showOlderVersions || index === 0)
        .map((item, index) => (
          <DownloadSeparator key={item.title}>
            <Typography variant="h4" component="h2">
              {item.type.label} {item.title}
            </Typography>

            <DownloadDate>{item.date}</DownloadDate>

            <DownloadButtons>
              {Object.entries(item.files)
                .filter(([key]) => key !== 'standalone')
                .map(([fileType, file], index) => (
                  <Button
                    key={fileType}
                    href={(file as { fileUrl: string }).fileUrl}
                    icon={
                      fileType === 'mac'
                        ? 'apple'
                        : fileType === 'win'
                        ? 'windows'
                        : fileType === 'linux'
                        ? 'linux'
                        : undefined
                    }
                    label={
                      (fileType !== 'all' && index === 0 ? t('downloads-button-download-for') + ' ' : '') +
                      (fileType === 'mac'
                        ? 'MacOS'
                        : fileType === 'win'
                        ? 'Windows'
                        : fileType === 'linux'
                        ? 'Linux'
                        : t('downloads-button-download'))
                    }
                    size="large"
                    color={index === 0 ? 'primary' : 'secondary'}
                  />
                ))}
            </DownloadButtons>
            <DownloadDescription>
              <p>
                {t('downloads-changes-in')} {item.title}
              </p>

              <div
                dangerouslySetInnerHTML={{
                  __html: item.content,
                }}
              />
            </DownloadDescription>
            <DownloadLinks>
              {item.releaseLogUrl && productSlug !== 'prusaslicer' && (
                <Link
                  href={
                    index === 0
                      ? `/changelog/${productSlug}`
                      : `/changelog/${productSlug}#version-${slugify(item.title)}`
                  }
                >
                  {t('downloads-full-release')}
                </Link>
              )}
              {items.length > 1 && index === 0 && (
                <a onClick={() => updateOldQuery(!showOlderVersions)}>
                  {showOlderVersions ? t('downloads-hide-older-version') : t('downloads-older-versions')}
                </a>
              )}
            </DownloadLinks>
          </DownloadSeparator>
        ))}
    </DownloadWrap>
  );
}
